import { useState, useCallback } from 'react';
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';


export function useBillProcessing(organizationId, setOrders) {
  // State for Bill Upload Page Selection Modal
  const [showBillPageModal, setShowBillPageModal] = useState(false);
  const [pendingBillOrderId, setPendingBillOrderId] = useState(null);
  const [billPagesToProcess, setBillPagesToProcess] = useState('');

  // Process bill with Document AI
  const processBillWithDocumentAI = async (file, orderId, pagesToProcess, replaceIndex = null, isReplacing = false) => {
    try {
      // TEMPORARY DISABLE: Document AI processing is temporarily disabled
      // Users must manually enter order values
      
      // COMMENTED OUT: Original Document AI processing code
      /*
      // Convert file to base64 for Document AI processing
      const arrayBuffer = await file.arrayBuffer();
      const base64String = btoa(
        new Uint8Array(arrayBuffer)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      
      // Call Document AI Cloud Function
      const functions = getFunctions();
      const processDocument = httpsCallable(functions, 'processDocument');
      
      const response = await processDocument({
        fileBuffer: base64String,
        mimeType: file.type,
        pagesToProcess: pagesToProcess
      });
      
      const { data: processedResult } = response;
      
      if (processedResult.success && processedResult.data) {
        const extractedData = processedResult.data;
        
        // Extract total amount from the processed data
        let totalAmount = null;
        if (extractedData.total_amount) {
          totalAmount = extractedData.total_amount;
        } else if (extractedData.total) {
          totalAmount = extractedData.total;
        } else if (extractedData.tax_invoice) {
          totalAmount = extractedData.tax_invoice;
        }
        
        // Update order with extracted total amount if found
        if (totalAmount) {
          const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
          // Fetch current orderBillValues array
          let billValues = [];
          const numericTotal = typeof totalAmount === 'string' ? parseFloat(totalAmount.toString().replace(/,/g, '')) : Number(totalAmount);
          if (isReplacing && replaceIndex === null) {
            // Replace all: reset array to only the new value
            billValues = [numericTotal];
          } else {
            // Fetch current array for append/replace
            const orderSnap = await getDoc(orderRef);
            if (orderSnap.exists() && Array.isArray(orderSnap.data().orderBillValues)) {
              billValues = [...orderSnap.data().orderBillValues];
            }
            if (replaceIndex !== null && !isNaN(replaceIndex) && replaceIndex >= 0 && replaceIndex < billValues.length) {
              billValues[replaceIndex] = numericTotal;
            } else {
              billValues.push(numericTotal);
            }
          }
          // Calculate new sum
          const newOrderValue = billValues.reduce((sum, val) => sum + (Number(val) || 0), 0);
          await updateDoc(orderRef, {
            orderBillValues: billValues,
            orderValue: newOrderValue,
            orderValueStatus: 'pending',
            updatedAt: serverTimestamp()
          });
          // Refresh order data in state
          const updatedOrderDoc = await getDoc(orderRef);
          if (updatedOrderDoc.exists()) {
            const updatedOrder = updatedOrderDoc.data();
            setOrders(prevOrders => {
              if (Array.isArray(prevOrders)) {
                return prevOrders.map(order => 
                  order.id === orderId ? { ...order, ...updatedOrder, orderValueStatus: 'pending' } : order
                );
              }
              return prevOrders;
            });
          }
          return { success: true, totalAmount: newOrderValue, message: `Bill uploaded and processed successfully! Extracted total amount: ₹${newOrderValue}` };
        } else {
          return { success: true, totalAmount: null, message: 'Bill uploaded successfully! No total amount could be extracted from the document.' };
        }
      } else {
        return { success: false, error: 'Document processing failed. Please try again.' };
      }
      */
      
      // TEMPORARY IMPLEMENTATION: Set default order value to 0 and require manual editing
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      
      // Fetch current orderBillValues array
      let billValues = [];
      const orderSnap = await getDoc(orderRef);
      if (orderSnap.exists() && Array.isArray(orderSnap.data().orderBillValues)) {
        billValues = [...orderSnap.data().orderBillValues];
      }
      
      // Add 0 as default value for new bill
      if (isReplacing && replaceIndex === null) {
        // Replace all: reset array to only the new value
        billValues = [0];
      } else if (replaceIndex !== null && !isNaN(replaceIndex) && replaceIndex >= 0 && replaceIndex < billValues.length) {
        billValues[replaceIndex] = 0;
      } else {
        billValues.push(0);
      }
      
      // Calculate new sum (will be 0 for new bills)
      const newOrderValue = billValues.reduce((sum, val) => sum + (Number(val) || 0), 0);
      
      await updateDoc(orderRef, {
        orderBillValues: billValues,
        orderValue: newOrderValue,
        orderValueStatus: 'pending',
        updatedAt: serverTimestamp()
      });
      
      // Refresh order data in state
      const updatedOrderDoc = await getDoc(orderRef);
      if (updatedOrderDoc.exists()) {
        const updatedOrder = updatedOrderDoc.data();
        setOrders(prevOrders => {
          if (Array.isArray(prevOrders)) {
            return prevOrders.map(order => 
              order.id === orderId ? { ...order, ...updatedOrder, orderValueStatus: 'pending' } : order
            );
          }
          return prevOrders;
        });
      }
      
      return { 
        success: true, 
        totalAmount: 0, 
        message: 'Bill uploaded successfully! Please manually edit the order value amount.' 
      };
      
    } catch (error) {
      return { success: false, error: 'There was an error processing the document. Please try again.' };
    }
  };

  // Show bill page selection modal
  const showBillPageModalForOrder = useCallback((orderId) => {
    setPendingBillOrderId(orderId);
    setShowBillPageModal(true);
  }, []);

  // Close bill page selection modal
  const closeBillPageModal = useCallback(() => {
    setShowBillPageModal(false);
    setPendingBillOrderId(null);
    setBillPagesToProcess('');
  }, []);

  // Handle pages input change
  const handlePagesChange = useCallback((e) => {
    setBillPagesToProcess(e.target.value);
  }, []);

  // Confirm bill page selection and trigger processing
  const confirmBillPageSelection = useCallback((handleFileUpload) => {
    setShowBillPageModal(false);
    if (pendingBillOrderId) {
      const pagesToProcess = billPagesToProcess ? parseInt(billPagesToProcess, 10) : undefined;
      handleFileUpload(pendingBillOrderId, 'bill', true, false, null, pagesToProcess);
      setPendingBillOrderId(null);
      setBillPagesToProcess('');
    }
  }, [pendingBillOrderId, billPagesToProcess]);

  // Check if confirm button should be disabled
  const isConfirmDisabled = billPagesToProcess && parseInt(billPagesToProcess, 10) < 1;

  return {
    // State
    showBillPageModal,
    pendingBillOrderId,
    billPagesToProcess,
    setShowBillPageModal,
    setPendingBillOrderId,
    setBillPagesToProcess,
    // Actions
    processBillWithDocumentAI,
    showBillPageModalForOrder,
    closeBillPageModal,
    confirmBillPageSelection,
    // UI - Return modal props instead of component
    billPageModalProps: {
      isOpen: showBillPageModal,
      onClose: closeBillPageModal,
      pagesToProcess: billPagesToProcess,
      onPagesChange: handlePagesChange,
      onConfirm: confirmBillPageSelection,
      isConfirmDisabled
    }
  };
} 